import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import useReadState from '@hooks/useReadState'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateRangeField, choiceField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { buildGlBudgetGrid, budgetSections } from './glBudgetMatrix'
import { ROLLUP_UNAVAILABLE_REASONS } from './plStatement'

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF (must render inside the shell's context provider).
// This is what gives GL Budget its first export path — the report previously
// had NO export at all.
//
// Spec 143: a STATEMENT, like the P&L and the Balance Sheet (spec 127) — sections
// never collapse, headers never sort (a sort interleaved parents, children and
// their "Total {parent}" lines; measured 2026-10-08), and each parent account rolls
// up to CB's own roll-up figures, one by one or all at once. A sort persisted
// under report:gl-budget is ignored by the grid, not deleted.
const PublishedGroupedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
      statementMode
    />
  )
}

const makeDefaults = () => ({
  budgetId: 0,
  interval: 'Monthly',
  startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  isCashBasis: true,
})

// The wire boundary. `accountSegment: 0` has no control and must survive every
// edit; `budgetId` falls back to 0, which is the "nothing chosen" sentinel the run
// guard checks.
const toWire = (f) => ({
  budgetId: f.budgetId || 0,
  interval: f.interval,
  startDate: f.startDate,
  endDate: f.endDate,
  accountSegment: 0,
  isCashBasis: f.isCashBasis,
})

/**
 * Built per render: the Budget picker's options are SERVER-loaded (and the page
 * auto-selects the first budget once they arrive, which the migration preserves).
 *
 * Budget and Interval are mutually exclusive choices → RADIO. omitAll on the range
 * because a budget-vs-actual comparison needs a bounded window.
 */
const buildFields = (budgets) => [
  choiceField({
    key: 'budget', label: 'Budget', valueKey: 'budgetId',
    options: [
      { value: 0, label: 'Select a budget…' },
      ...budgets.map((b) => ({ value: b.budgetId || b.id, label: b.name || b.budgetName })),
    ],
    decode: (s) => parseInt(s, 10) || 0,
  }),
  choiceField({
    key: 'interval', label: 'Interval', valueKey: 'interval',
    options: [
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Quarterly', label: 'Quarterly' },
      { value: 'Yearly', label: 'Yearly' },
    ],
  }),
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'startDate', endKey: 'endDate', omitAll: true }),
]

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const GLBudgetReport = ({ reportId, name = 'GL Budget vs Actual', summary, jobId }) => {
  /*
   * CC-53132 — this list is the page's single point of failure, and it used to
   * fail invisibly.
   *
   * The ONLY thing that ever moves `budgetId` off its 0 sentinel is the
   * auto-select effect below, which is driven by `budgets.length`. The run guard
   * refuses to run without a budgetId. So a read of the headers that never
   * completes leaves the page saying "Select a budget to view the report" while
   * the picker holds nothing but its placeholder — an instruction the user
   * physically cannot follow, with no spinner and no error, indefinitely.
   *
   * `isLoading` cannot see that: it is `isPending && isFetching`, so it is FALSE
   * for a query that is pending but NOT in flight — above all a PAUSED one
   * (react-query's default `networkMode: 'online'` while `onlineManager`
   * believes the browser is offline). `queryFn` is never called, so there is no
   * request to find in any log, on either side. Hence `readState`, and hence the
   * hook rather than the bare function: it carries the permanent blocked-read
   * diagnostic.
   */
  const budgetsQuery = useQuery({
    queryKey: ['gl-budget-headers'],
    queryFn: () => accountingReportService.glBudget.getBudgetHeaders(),
    staleTime: 60_000,
  })
  const budgetsState = useReadState('gl-budget-headers', budgetsQuery)
  const budgetsReady = budgetsState === 'ready'
  // Coerced only to build the picker's options and drive the auto-select below.
  // `budgetsReady` is the predicate that decides whether an empty picker may be
  // DESCRIBED to the user, which is the half that was wrong.
  //
  // Memoized because this array is a dependency of the auto-select effect and of
  // the `fields` memo: a fresh `[]` every render is a ticking value reaching a
  // columns/fields memo, which this repo has been bitten by twice. (The
  // destructuring default it replaces had the same flaw, just invisibly.)
  const budgets = useMemo(() => budgetsQuery.data ?? [], [budgetsQuery.data])

  const [filters, setFilters] = useState(makeDefaults)

  // Pick the first budget once headers load
  useEffect(() => {
    if (budgets.length > 0 && !filters.budgetId) {
      // Pre-existing. Surfaced, not introduced, by spec 131: the React Compiler
      // lint skipped this whole component while its runReport held a
      // try/finally, which now lives in useLatestRun.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((f) => ({ ...f, budgetId: budgets[0].budgetId || budgets[0].id }))
    }
  }, [budgets, filters.budgetId])

  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria — the budget picker re-runs and is never disabled
  // while a run is out. Spinner immediately when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.startDate, filters.endDate)), [filters])
  const [report, setReport] = useState(null)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const params = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => {
    const jid = jobRef.current
    // No budget, nothing to run. Spec 131: and a run still in flight for the
    // PREVIOUS budget is retired rather than left to land. Silent, as before — the
    // page's own "Select a budget" prompt says why.
    if (!jid && !filters.budgetId) { invalidateRun(); return undefined }
    return startRun(async () => {
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the saved snapshot (renders even though no budget
        // is selected — the grid shows whenever there's a report). Loaded regardless of
        // the budget guard above.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        // CC-53132: `|| null` here left `report` null with no budget selected, so
        // the page fell through to "Select a budget to view the report" — under a
        // DATED saved-result banner, for a snapshot it had simply failed to read.
        // A run that genuinely found nothing still carries `report` (that is what
        // CC-50252's "returned no accounts" message renders from), so its absence
        // means the body was unreadable, not that the budget was empty.
        if (!r?.data?.report) {
          throw new Error('That saved result could not be read. Run the report again to rebuild it.')
        }
        return () => { setSavedAsOf(st?.createdAt || null); setReport(r.data.report) }
      }
      const data = await accountingReportService.glBudget.run(params, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setReport(data?.report || null) }
    }, { criteria: { filtersSummary } })
  }, [params, filters.budgetId, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so an effect pass torn down before the
  // timer fires (StrictMode's simulated remount) starts no run.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [filters.budgetId])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50252: read CB's ACTUAL keys. This page previously read
  // `glBudgetSections` / `glActualVsBudgetHeaders`, which the payload has never
  // carried — cb-api's camelize turns CB's `GLBudgetSections` into
  // `gLBudgetSections` (capital L), so both resolved to [] and the report
  // rendered with no rows and no columns. `budgetSections` tolerates both.
  const sections = useMemo(() => budgetSections(report), [report])

  // GUARDRAIL: budget-statement ordering is semantic (Income before Expenses
  // …). Sections map 1:1 to grid groups IN SERVER ORDER — no flattening, and no
  // sorting at all (spec 143: statement mode). CB's per-SECTION totals (Total
  // Income, Total Expenses, Net Income …) arrive as the totals row CB ends each
  // section with and render as that section's footer — this comment used to say
  // the payload carries none, which was wrong (spec 143). There is no REPORT-level
  // total, and mixed actual/budget/variance columns are not plain-additive across
  // accounts, so nothing is summed or fabricated. Every cell is a server value
  // passed through; see glBudgetMatrix.js for the field map and V1's layout.
  const gridReport = useMemo(() => buildGlBudgetGrid(report), [report])

  // Nothing for a grid to draw: no budget chosen and no reopened snapshot.
  const noBudget = !filters.budgetId && !report

  // CC-50013 G2 / CC-50148: shared metadata-driven bar. Built per render because
  // the Budget picker's options are server-loaded. `params` above stays the wire
  // boundary — including `accountSegment: 0`, which has no control.
  const fields = useMemo(() => buildFields(budgets), [budgets])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      disabled={!filters.budgetId}
      queue={{
        reportId,
        name,
        // Mirrors accountingReportService.glBudget.run: an ACCOUNTING job (GET
        // replay), key 'gl-budget' — per-user server-side (bank-account security).
        kind: 'accounting',
        accountingKey: 'gl-budget',
        endpoint: (f) => accountingEndpoint('/reports/accounting/gl-budget', toWire(f)),
        filters: (f) => toWire(f),
        // Spec 194: the emailed copy can carry this page's own export (CSV only).
        attachFormats: accountingAttachFormats('/reports/accounting/gl-budget'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey={'report:gl-budget'}
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      // Export fix: CSV proxies the legacy Accounting/DownloadGLBudgetVsActualCSV
      // endpoint (no Excel endpoint exists in CB for this report); PDF comes
      // from the published grid view (client-built).
      exportFormats={['csv']}
      download={filters.budgetId
        ? (format, getToken) => accountingReportService.glBudget.download(params, format, getToken)
        : undefined}
    >
      {/* CC-50252: this report rendered an empty grid for months and said
          nothing, because the page read keys the payload never carried. A run
          that comes back with no sections now SAYS so rather than showing a
          bare grid that looks like a working report with no data in it. */}
      {!running && report && sections.length === 0 && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
          <span>
            <strong className="font-semibold">This budget returned no accounts.</strong>{' '}
            Check the date range covers the budget&apos;s fiscal year — a window outside it
            produces a report with nothing in it.
          </span>
        </div>
      )}
      {/* Spec 143 (the P&L's spec 127 §8 note, same words): the builder refused the
          account tree — an id CB sent twice, or none — so the roll-up controls are
          absent. Said, rather than leaving them silently missing. Never observed on
          vladdemo. amber-900 on amber-50 is 8.77:1, as on the sibling notes. */}
      {!noBudget && gridReport.rollupUnavailable && (
        <div
          role="status"
          data-testid="glb-rollup-unavailable"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
          <span>{`Roll-up is unavailable for this run: ${ROLLUP_UNAVAILABLE_REASONS[gridReport.rollupUnavailable]}`}</span>
        </div>
      )}
      {!noBudget && (
        <PublishedGroupedGrid key={view.gridKey} report={gridReport} storageKey="report:gl-budget" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
      )}

      {/* CC-53132: what stands in for the grid is now decided by the STATE of the
          headers read, not by its data — because the prompt below is only a true
          statement in ONE of those states, and it used to be shown in all of
          them. Four branches (loading and idle share one; see the last). */}

      {/* The real thing: the picker is loaded and holds whatever the firm has, so
          "you have not chosen yet" is an accurate description. A firm with no
          budgets at all also lands here — that is a genuine empty answer and must
          keep reading as one.

          `!error` because a reopened saved result that could not be read also
          leaves no budget selected, and telling the user to pick one when the
          failure had nothing to do with their choice sends them the wrong way. */}
      {noBudget && budgetsReady && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-3 py-12 text-center text-sm text-gray-500">
          Select a budget to view the report
        </div>
      )}

      {/* Paused: no request was sent, so nothing here is known. `role="alert"`
          because the user is looking at a page that appeared to ask them for
          something and has to be told that is not what happened. */}
      {noBudget && budgetsState === 'blocked' && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can’t reach the server, so we haven’t loaded your budgets.</p>
          <p className="mt-1 text-amber-800">
            This is not a firm with no budgets, and it is not a budget you forgot to pick — the
            list the Budget picker draws from was never read. Your connection looks offline;
            check it and try again.
          </p>
          <button
            type="button"
            onClick={() => budgetsQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* Failed: the headers read threw. Its error had no render site anywhere on
          this page before — the only `error` here is local RUN state, and a run
          never starts without a budget, so the failure was silent by
          construction. */}
      {noBudget && budgetsState === 'failed' && (
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
          <p className="font-medium">We couldn’t load your budgets: {budgetsQuery.error?.message}</p>
          <p className="mt-1">
            This is not a firm with no budgets — nothing was read, so no budget can be selected
            and no report has been run.
          </p>
          <button
            type="button"
            onClick={() => budgetsQuery.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* Still reading. `idle` rides along deliberately: this query has no
          `enabled` option today, so it cannot reach that state — but every branch
          here is gated on a named state, and an unhandled fifth one renders
          NOTHING, which is a blank page for whoever adds an `enabled` gate later.
          Safe to fold in because the shell owns the RUN spinner (via `running`)
          and hides these children while it shows, so this can never become the
          page's permanent spinner. */}
      {noBudget && (budgetsState === 'loading' || budgetsState === 'idle') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-3 py-12 text-center text-sm text-gray-500">
          Loading your budgets…
        </div>
      )}
    </SpecializedReportShell>
  )
}

export default GLBudgetReport
